import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const fetchMe = async (t) => {
    const response = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${t}` }
    });
    return response.data;
  };

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const data = await fetchMe(token);
          setUser(data);
        } catch (error) {
          console.error('Auth init error:', error);
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    
    if (response.data.requires_role_selection) {
      return { requires_role_selection: true, roles: response.data.roles };
    }
    
    const { access_token } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    const userData = await fetchMe(access_token);
    setUser(userData);
    return userData;
  };

  const loginWithRole = async (userId) => {
    const response = await axios.post(`${API}/auth/login-with-role`, { user_id: userId });
    const { access_token } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    const userData = await fetchMe(access_token);
    setUser(userData);
    return userData;
  };

  const impersonate = async (userId) => {
    const response = await axios.post(`${API}/auth/impersonate/${userId}`, null, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const { access_token } = response.data;
    localStorage.setItem('admin_token', token);
    localStorage.setItem('token', access_token);
    setToken(access_token);
    const userData = await fetchMe(access_token);
    setUser(userData);
    return userData;
  };

  const stopImpersonating = async () => {
    try {
      const response = await axios.post(`${API}/auth/stop-impersonate`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { access_token } = response.data;
      localStorage.removeItem('admin_token');
      localStorage.setItem('token', access_token);
      setToken(access_token);
      const userData = await fetchMe(access_token);
      setUser(userData);
      return userData;
    } catch {
      // Fallback: use stored admin token
      const adminToken = localStorage.getItem('admin_token');
      if (adminToken) {
        localStorage.removeItem('admin_token');
        localStorage.setItem('token', adminToken);
        setToken(adminToken);
        const userData = await fetchMe(adminToken);
        setUser(userData);
        return userData;
      }
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('admin_token');
    setToken(null);
    setUser(null);
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    return response.data;
  };

  const value = {
    user,
    token,
    loading,
    login,
    loginWithRole,
    logout,
    register,
    impersonate,
    stopImpersonating,
    isAuthenticated: !!user,
    isImpersonating: !!user?.impersonating,
    isAdmin: user?.role === 'admin',
    isContractor: user?.role === 'contractor',
    isWorker: user?.role === 'worker',
    isClient: user?.role === 'client',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
